"use client";

import { useCallback, useRef } from "react";
import { useSnapshot } from "valtio";
import { rootStore } from "@store/rootStore";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { buildGoalDetailPath } from "@utils/goalRoute";
import { closeActiveChatOverlay } from "@utils/chatUtils/closeActiveChatOverlay";
import {
  readGoalActivationContinuation,
  saveGoalActivationContinuation,
  clearGoalActivationContinuation,
} from "./goalActivationContinuation";
import { useQueryClient } from "@tanstack/react-query";
import { openOnboardingPaywall } from "@components/ChatOnboarding/openOnboardingPaywall";
import { useIsPc } from "@hooks/useIsPc";
import { useMemberUser } from "@hooks/useMemberUser";
import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";
import { requestNativeSurface } from "@features/nativeSurface/requestNativeSurface";
import { getBridge } from "@/src/lib/bridge";
import { QUERY_KEYS } from "@config/constant";
import { Track } from "@/src/lib/tracking";
import { requestGoalActivationSuccessSurface } from "@/src/components/GoalConversion/goalActivationSuccessSurfaceStore";
import { goalSignalQueryKeys } from "./goalSignalQueryKeys";
import { goalQueryKeys } from "./queryKeys";

export type GoalActivationSource = "chat" | "signal" | "plan" | "my_goals";

export type GoalActivationParams = {
  goalId: string;
  goalTitle?: string;
  source: GoalActivationSource;
  idempotencyKey?: string;
  navigateOnSuccess?: boolean;
};

export type GoalActivationOutcome =
  | { outcome: "activated"; goalId: string }
  | { outcome: "dismissed" }
  | {
      outcome: "retry_required";
      goalId: string;
      goalStatus?: string;
      runtimeStatus?: "RECOVERING" | "FAILED";
      retryAfterMs?: number;
      correlationId?: string;
    }
  | {
      outcome: "error";
      reason: "activation_not_ready" | "activation_failed" | "entitlement_sync";
    };

type GoalActivationError = {
  code?: string;
  goalId?: string;
  goalStatus?: string;
  runtimeStatus?: "RECOVERING" | "FAILED";
  retryAfterMs?: number;
  correlationId?: string;
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function parseActivationError(error: unknown): GoalActivationError {
  const response = asRecord(error);
  const axiosResponse = asRecord(response?.response);
  const body =
    asRecord(axiosResponse?.data) ??
    asRecord(response?.error) ??
    asRecord(response?.data) ??
    response;
  if (!body) {
    return {};
  }
  const runtimeStatus = body.runtimeStatus;
  return {
    code: typeof body.code === "string" ? body.code : undefined,
    goalId: typeof body.goalId === "string" ? body.goalId : undefined,
    goalStatus:
      typeof body.goalStatus === "string" ? body.goalStatus : undefined,
    runtimeStatus:
      runtimeStatus === "RECOVERING" || runtimeStatus === "FAILED"
        ? runtimeStatus
        : undefined,
    retryAfterMs:
      typeof body.retryAfterMs === "number" && body.retryAfterMs > 0
        ? body.retryAfterMs
        : undefined,
    correlationId:
      typeof body.correlationId === "string" ? body.correlationId : undefined,
  };
}

function isRefreshedMemberEntitled(member: unknown): boolean {
  const record = asRecord(member);
  if (!record) {
    return false;
  }
  if (typeof record.isMember === "boolean") {
    return record.isMember;
  }
  const product = asRecord(record.product);
  const level = typeof product?.level === "string" ? product.level : undefined;
  return record.status === "ACTIVE" && level !== undefined && level !== "FREE";
}

function createAttemptKey(
  goalId: string,
  source: GoalActivationSource,
): string {
  const randomId =
    typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${goalId}:${source}:${randomId}`;
}

function entitlementSyncFailed(
  goalId: string,
  source: GoalActivationSource,
): GoalActivationOutcome {
  Track.event("goal_activate_failed", {
    goal_id: goalId,
    source,
    error_code: "ENTITLEMENT_SYNC_PENDING",
    stage: "entitlement_sync",
  });
  return { outcome: "error", reason: "entitlement_sync" };
}

export function useGoalActivationFlow() {
  const { chatApi } = useOspApiClientProvider();
  const { ownerInfo } = useSnapshot(rootStore.user);
  const ownerId = ownerInfo?.id ?? "";
  const router = useShellRouter();
  const navigateToGoal = useCallback(
    (goalId: string, source: GoalActivationSource) => {
      const href = buildGoalDetailPath(goalId);
      const navigate = () => {
        if (!window.location.pathname.replace(/\/$/, "").endsWith(href)) {
          router.push(href);
        }
      };
      if (source === "chat") {
        closeActiveChatOverlay({ onClosed: navigate });
      } else {
        navigate();
      }
    },
    [router],
  );
  const { data: member, refetch: refetchMember } = useMemberUser();
  const [isPC] = useIsPc();
  const queryClient = useQueryClient();
  const entitledAttempts = useRef(new Set<string>());

  const refreshEntitlement = useCallback(async () => {
    try {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MEMBER_USE_PLAN],
      });
    } catch {
      /* A direct member read below can still confirm entitlement. */
    }
    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        const refreshed = await refetchMember();
        if (isRefreshedMemberEntitled(refreshed.data)) {
          return true;
        }
      } catch {
        /* Receipt processing and member reads can briefly lag behind payment. */
      }
      if (attempt < 4) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
    return false;
  }, [queryClient, refetchMember]);

  const purchaseAndRefreshEntitlement = useCallback(
    async ({
      goalId,
      goalTitle,
      source,
      attemptKey,
    }: {
      goalId: string;
      goalTitle?: string;
      source: GoalActivationSource;
      attemptKey: string;
    }): Promise<"ready" | "dismissed" | "not_ready"> => {
      let redirected = false;
      const continuation = {
        ownerId,
        goalId,
        goalTitle,
        source,
        idempotencyKey: attemptKey,
        createdAt: Date.now(),
      };
      try {
        const paywall = await openOnboardingPaywall(isPC, {
          paywallVariant: "goal_activate_second",
          analyticsSource: "goal_activate",
          goalId,
          source,
          onWebCheckoutRedirect: () => {
            redirected = true;
            saveGoalActivationContinuation({
              ...continuation,
              phase: "checkout",
            });
          },
        });
        if (paywall !== "purchased") {
          if (!redirected) {
            clearGoalActivationContinuation(ownerId, attemptKey);
          }
          return "dismissed";
        }
        saveGoalActivationContinuation({
          ...continuation,
          phase: "activating",
        });
        if (!(await refreshEntitlement())) {
          return "not_ready";
        }
        entitledAttempts.current.add(attemptKey);
        return "ready";
      } catch {
        if (await refreshEntitlement()) {
          saveGoalActivationContinuation({
            ...continuation,
            phase: "activating",
          });
          entitledAttempts.current.add(attemptKey);
          return "ready";
        }
        return "not_ready";
      }
    },
    [isPC, ownerId, refreshEntitlement],
  );

  const activate = useCallback(
    async ({
      goalId,
      goalTitle,
      source,
      idempotencyKey,
      navigateOnSuccess = true,
    }: GoalActivationParams): Promise<GoalActivationOutcome> => {
      if (!goalId || !chatApi?.goals) {
        return { outcome: "error", reason: "activation_failed" };
      }
      Track.event("goal_activate_start", {
        goal_id: goalId,
        source,
        member_state: member?.isMember === true ? "member" : "non_member",
      });
      const pending = readGoalActivationContinuation(ownerId);
      const resuming =
        pending?.goalId === goalId && pending.phase === "activating";
      const attemptKey =
        idempotencyKey ??
        (pending?.goalId === goalId ? pending.idempotencyKey : undefined) ??
        createAttemptKey(goalId, source);
      if (resuming && !entitledAttempts.current.has(attemptKey)) {
        if (!(await refreshEntitlement())) {
          return entitlementSyncFailed(goalId, source);
        }
        entitledAttempts.current.add(attemptKey);
      }
      if (
        member?.isMember !== true &&
        !entitledAttempts.current.has(attemptKey)
      ) {
        const entitlement = await purchaseAndRefreshEntitlement({
          goalId,
          goalTitle,
          source,
          attemptKey,
        });
        if (entitlement === "dismissed") {
          return { outcome: "dismissed" };
        }
        if (entitlement === "not_ready") {
          return entitlementSyncFailed(goalId, source);
        }
      }
      if (rootStore.user.ownerInfo?.id !== ownerId) {
        return { outcome: "dismissed" };
      }
      saveGoalActivationContinuation({
        ownerId,
        goalId,
        goalTitle,
        source,
        idempotencyKey: attemptKey,
        phase: "activating",
        createdAt: pending?.goalId === goalId ? pending.createdAt : Date.now(),
      });
      for (;;) {
        if (rootStore.user.ownerInfo?.id !== ownerId) {
          return { outcome: "dismissed" };
        }
        try {
          const response = await chatApi.goals.activateCreate(goalId, {
            source,
            idempotencyKey: attemptKey,
          });
          const data = response.data;
          if (data?.status !== "ACTIVE" || data.runtimeStatus !== "READY") {
            Track.event("goal_activate_failed", {
              goal_id: goalId,
              source,
              error_code: "ACTIVATION_NOT_READY",
              stage: "activate",
            });
            return { outcome: "error", reason: "activation_not_ready" };
          }
          clearGoalActivationContinuation(ownerId, attemptKey);
          await Promise.allSettled([
            queryClient.invalidateQueries({ queryKey: goalQueryKeys.all }),
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.GOAL_DETAIL, goalId],
            }),
            queryClient.invalidateQueries({ queryKey: goalQueryKeys.stats() }),
            queryClient.invalidateQueries({
              queryKey: goalSignalQueryKeys.all,
            }),
          ]);
          const attemptId = data.correlationId ?? attemptKey;
          if (getBridge().isInApp()) {
            requestNativeSurface({
              key: "goal_activation_success",
              payload: { goalId, goalTitle: goalTitle ?? "", source },
            });
          } else {
            requestGoalActivationSuccessSurface({
              goalId,
              goalTitle: goalTitle ?? "",
              attemptId,
            });
          }
          Track.event("goal_activate_success", {
            goal_id: goalId,
            source,
            correlation_id: data.correlationId,
          });
          if (navigateOnSuccess) {
            navigateToGoal(goalId, source);
          }
          return { outcome: "activated", goalId };
        } catch (error) {
          const activationError = parseActivationError(error);
          if (
            activationError.code === "PAYMENT_REQUIRED" &&
            !entitledAttempts.current.has(attemptKey)
          ) {
            const entitlement = await purchaseAndRefreshEntitlement({
              goalId,
              goalTitle,
              source,
              attemptKey,
            });
            if (entitlement === "dismissed") {
              return { outcome: "dismissed" };
            }
            if (entitlement === "not_ready") {
              return entitlementSyncFailed(goalId, source);
            }
            continue;
          }
          if (activationError.code === "ACTIVATION_RETRY_REQUIRED") {
            Track.event("goal_activate_failed", {
              goal_id: goalId,
              source,
              error_code: activationError.code,
              stage: "activate",
            });
            return {
              outcome: "retry_required",
              goalId: activationError.goalId ?? goalId,
              ...(activationError.goalStatus
                ? { goalStatus: activationError.goalStatus }
                : {}),
              ...(activationError.runtimeStatus
                ? { runtimeStatus: activationError.runtimeStatus }
                : {}),
              ...(activationError.retryAfterMs
                ? { retryAfterMs: activationError.retryAfterMs }
                : {}),
              ...(activationError.correlationId
                ? { correlationId: activationError.correlationId }
                : {}),
            };
          }
          Track.event("goal_activate_failed", {
            goal_id: goalId,
            source,
            error_code: activationError.code ?? "ACTIVATION_FAILED",
            stage: "activate",
          });
          return { outcome: "error", reason: "activation_failed" };
        }
      }
    },
    [
      chatApi,
      member?.isMember,
      purchaseAndRefreshEntitlement,
      queryClient,
      ownerId,
      refreshEntitlement,
      navigateToGoal,
    ],
  );

  return { activate, navigateToGoal };
}
